import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';

export type LanguageCode = 'ru' | 'en' | 'kk' | 'zh';

export interface Translations {
  [key: string]: any;
}

/**
 * Язык, из которого подставляется перевод, если ключ отсутствует в основном
 * языке. Русский и английский считаются полными (не выше этого справочника),
 * поэтому для них фолбэка нет — translate() просто вернёт сам ключ.
 */
const FALLBACK_LANGUAGE: Partial<Record<LanguageCode, LanguageCode>> = {
  kk: 'ru',
  zh: 'en'
};

const SUPPORTED_LANGUAGES: LanguageCode[] = ['ru', 'en', 'kk', 'zh'];

/** Языки стран СНГ, которых нет на сайте: таким посетителям русский понятнее английского. */
const CIS_LANGUAGES = ['uz', 'ky', 'tg', 'tk', 'az', 'hy', 'be', 'uk', 'ka'];

/**
 * Язык первого визита по настройкам браузера: kk/ru/en/zh — как есть,
 * языки СНГ — русский, остальные — английский. Без данных о браузере — русский.
 */
function detectBrowserLanguage(): LanguageCode {
  if (typeof navigator === 'undefined') return 'ru';
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  const codes = tags.filter(Boolean).map(tag => tag.toLowerCase().split('-')[0]);
  if (!codes.length) return 'ru';
  for (const code of codes) {
    if ((SUPPORTED_LANGUAGES as string[]).includes(code)) return code as LanguageCode;
    if (CIS_LANGUAGES.includes(code)) return 'ru';
  }
  return 'en';
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private currentLanguage$ = new BehaviorSubject<LanguageCode>('ru');
  private translations$ = new BehaviorSubject<Translations>({});
  private translationsCache: Map<LanguageCode, Translations> = new Map();

  constructor(private http: HttpClient) {
    // Язык, выбранный ранее на сайте; если его нет — определяем по языку браузера
    const saved = localStorage.getItem('language') as LanguageCode | null;
    const initial = saved && SUPPORTED_LANGUAGES.includes(saved) ? saved : detectBrowserLanguage();
    this.currentLanguage$.next(initial);
    // Загружаем переводы сразу
    this.setLanguage(initial);
  }

  getCurrentLanguage(): Observable<LanguageCode> {
    return this.currentLanguage$.asObservable();
  }

  getTranslations(): Observable<Translations> {
    return this.translations$.asObservable();
  }

  setLanguage(lang: LanguageCode): void {
    const fallbackLang = FALLBACK_LANGUAGE[lang];

    if (this.translationsCache.has(lang) && (!fallbackLang || this.translationsCache.has(fallbackLang))) {
      // Используем кэш (основной язык и, если нужен, его фолбэк уже загружены)
      this.translations$.next(this.translationsCache.get(lang)!);
      this.currentLanguage$.next(lang);
      localStorage.setItem('language', lang);
      return;
    }

    const activate = (translations: Translations) => {
      this.translationsCache.set(lang, translations);
      this.translations$.next(translations);
      this.currentLanguage$.next(lang);
      localStorage.setItem('language', lang);
    };

    const onError = (err: unknown) => {
      console.error(`Failed to load translations for ${lang}:`, err);
      // Fallback на русский
      if (lang !== 'ru') {
        this.setLanguage('ru');
      }
    };

    if (!fallbackLang) {
      this.http.get<Translations>(`/assets/i18n/${lang}.json`).subscribe({
        next: (translations) => {
          console.log(`Translations loaded for ${lang}:`, translations);
          activate(translations);
        },
        error: onError
      });
      return;
    }

    // Языки с фолбэком (kk→ru, zh→en) — грузим оба файла и не переключаемся,
    // пока не будут в кэше оба: translate() ищет пропущенные ключи в фолбэке
    // синхронно, поэтому он должен быть готов до того, как компоненты начнут рендерить.
    const main$ = this.http.get<Translations>(`/assets/i18n/${lang}.json`);
    const fallback$ = this.translationsCache.has(fallbackLang)
      ? of(this.translationsCache.get(fallbackLang)!)
      : this.http.get<Translations>(`/assets/i18n/${fallbackLang}.json`);

    forkJoin({ main: main$, fallback: fallback$ }).subscribe({
      next: ({ main, fallback }) => {
        console.log(`Translations loaded for ${lang} (fallback: ${fallbackLang}):`, main);
        this.translationsCache.set(fallbackLang, fallback);
        activate(main);
      },
      error: onError
    });
  }

  translate(key: string, params?: { [key: string]: string | number }): string {
    let value = this.lookup(this.translations$.value, key);

    if (value === undefined) {
      const fallbackLang = FALLBACK_LANGUAGE[this.currentLanguage$.value];
      const fallbackTranslations = fallbackLang ? this.translationsCache.get(fallbackLang) : undefined;
      if (fallbackTranslations) {
        value = this.lookup(fallbackTranslations, key);
      }
    }

    if (value === undefined) {
      return key; // Возвращаем ключ, если перевод не найден нигде
    }

    // Заменяем параметры в строке
    if (params) {
      Object.keys(params).forEach(paramKey => {
        value = (value as string).replace(`{${paramKey}}`, String(params[paramKey]));
      });
    }

    return value;
  }

  private lookup(translations: Translations, key: string): string | undefined {
    const keys = key.split('.');
    let value: any = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }

    return typeof value === 'string' ? value : undefined;
  }

  getCurrentLanguageValue(): LanguageCode {
    return this.currentLanguage$.value;
  }
}
